import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import VendorsAPI from '../Api/vendor';

const useGetVendorOnboardingApplication = (vendorId: any) =>
  useQuery(
    ['vendor-application', vendorId],
    () => VendorsAPI.getApplication(vendorId),
    {
      onError: (error: any) => {
        toast(
          `Can not get application. ${JSON.stringify(error.errors) || error}`,
          {
            type: 'error',
          },
        );
      },
    },
  );

const useSaveApplicationWithoutValidation = () =>
  useMutation(
    (data: any) => VendorsAPI.saveApplicationWithoutValidation(data),
    {
      onError: () => {},
      onSuccess: () => {},
    },
  );

const useVendorValidateStep = () =>
  useMutation((data: any) => VendorsAPI.validateVendorStep(data), {
    onError: (error: any) => {
      toast(`Error validating step: ${JSON.stringify(error.errors) || error}`, {
        type: 'error',
      });
    },
    onSuccess: () => {
      toast('Step validated successfully', {
        type: 'success',
      });
    },
  });

const useBuyerValidateStep = () =>
  useMutation((data: any) => VendorsAPI.validateBuyerStep(data), {
    onError: (error: any) => {
      toast(`Error validating step: ${JSON.stringify(error.errors) || error}`, {
        type: 'error',
      });
    },
    onSuccess: () => {
      toast('Step validated successfully', {
        type: 'success',
      });
    },
  });

const useVendorSubmitApplication = () =>
  useMutation((data: any) => VendorsAPI.submitVendorApplication(data), {
    onError: (error: any) => {
      toast(
        `Error submitting application: ${
          JSON.stringify(error.errors) || error
        }`,
        {
          type: 'error',
        },
      );
    },
    onSuccess: () => {
      toast('Application submitted successfully', {
        type: 'success',
      });
    },
  });

const useBuyerSubmitApplication = () =>
  useMutation((data: any) => VendorsAPI.submitBuyerApplication(data), {
    onError: (error: any) => {
      toast(
        `Error submitting application: ${
          JSON.stringify(error.errors) || error
        }`,
        {
          type: 'error',
        },
      );
    },
    onSuccess: () => {
      toast('Application submitted successfully', {
        type: 'success',
      });
    },
  });

const useSponsorApproval = () => {
  const queryClient = useQueryClient();

  return useMutation((data: any) => VendorsAPI.sponsorApproval(data), {
    onError: (error: any) => {
      toast(
        `Error approving application: ${JSON.stringify(error.errors) || error}`,
        {
          type: 'error',
        },
      );
    },
    onSuccess: async (_data, variables) => {
      const status = variables.approved ? 'approved' : 'denied';
      toast(`Application ${status} successfully`, {
        type: 'success',
      });

      await queryClient.invalidateQueries(['vendor-application']);
    },
  });
};

const useChangeSponsorApproval = () => {
  const queryClient = useQueryClient();

  return useMutation((data: any) => VendorsAPI.changeSponsorApproval(data), {
    onError: (error: any) => {
      toast(
        `Error approving application: ${JSON.stringify(error.errors) || error}`,
        {
          type: 'error',
        },
      );
    },
    onSuccess: async () => {
      toast(`Application status changed successfully`, {
        type: 'success',
      });

      await queryClient.invalidateQueries(['vendor-application']);
    },
  });
};

const useFirstExecutiveApproval = () => {
  const queryClient = useQueryClient();

  return useMutation((data: any) => VendorsAPI.firstExecutiveApproval(data), {
    onError: (error: any) => {
      toast(
        `Error approving application: ${JSON.stringify(error.errors) || error}`,
        {
          type: 'error',
        },
      );
    },
    onSuccess: async (_data, variables: any) => {
      const status = variables.approved ? 'approved' : 'denied';
      toast(`Application ${status} successfully`, {
        type: 'success',
      });

      await queryClient.invalidateQueries(['vendor-application']);
    },
  });
};

const useSecondExecutiveApproval = () => {
  const queryClient = useQueryClient();

  return useMutation((data: any) => VendorsAPI.secondExecutiveApproval(data), {
    onError: (error: any) => {
      toast(
        `Error approving application: ${JSON.stringify(error.errors) || error}`,
        {
          type: 'error',
        },
      );
    },
    onSuccess: async (_data, variables: any) => {
      const status = variables.approved ? 'approved' : 'denied';
      toast(`Application ${status} successfully`, {
        type: 'success',
      });

      await queryClient.invalidateQueries(['vendor-application']);
    },
  });
};

const useGetVendorOnboardingSignedUrls = () =>
  useMutation(
    ({ vendorId, fileName }: any) =>
      VendorsAPI.getVendorOnboardingSignedUrls(vendorId, fileName),
    {
      onError: (error: any) => {
        toast(
          `Error getting file URLs: ${JSON.stringify(error.errors) || error}`,
          {
            type: 'error',
          },
        );
      },
      onSuccess: () => {},
    },
  );

const useArchiveApplication = () => {
  const queryClient = useQueryClient();

  return useMutation((data: any) => VendorsAPI.archiveApplication(data), {
    onError: (error: any) => {
      toast(
        `Error archiving application: ${JSON.stringify(error.errors) || error}`,
        {
          type: 'error',
        },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries('vendor-applications');
      toast(`Application archived successfully`, {
        type: 'success',
      });
    },
  });
};

const useUnArchiveApplication = () => {
  const queryClient = useQueryClient();

  return useMutation((data: any) => VendorsAPI.unArchiveApplication(data), {
    onError: (error: any) => {
      toast(
        `Error un-archiving application: ${JSON.stringify(error.errors) || error}`,
        {
          type: 'error',
        },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries('vendor-applications');
      toast(`Application un-archiving successfully`, {
        type: 'success',
      });
    },
  });
};

type UpdateVendorInfoData = {
  sponsor_id: number;
  existing_vendor: string;
  doing_business_as: string | null;
  legal_name: string;
  divisions: string[];
  primary_contact_firstname: string;
  primary_contact_lastname: string;
  primary_contact_title?: string;
};

type UpdateVendorInfoParams = {
  id: string;
  data: UpdateVendorInfoData;
};

const useUpdateVendorInfo = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, data }: UpdateVendorInfoParams) =>
      VendorsAPI.updateVendorInfo({ id, data }),
    {
      onError: () => {},
      onSuccess: async (res: any) => {
        await queryClient.invalidateQueries([
          'vendor-application',
          res.vendorId,
        ]);
      },
    },
  );
};

export {
  useUpdateVendorInfo,
  useGetVendorOnboardingApplication,
  useSaveApplicationWithoutValidation,
  useVendorValidateStep,
  useBuyerValidateStep,
  useVendorSubmitApplication,
  useBuyerSubmitApplication,
  useSponsorApproval,
  useFirstExecutiveApproval,
  useSecondExecutiveApproval,
  useGetVendorOnboardingSignedUrls,
  useChangeSponsorApproval,
  useArchiveApplication,
  useUnArchiveApplication,
};
